import SearchForm from '@components/organisms/forms/SearchForm';
import env from '@lib/constants/env';
import ROUTES from '@lib/constants/routes';
import useIsVisible from '@lib/hooks/visible';
import GridBox from 'components/atoms/GridBox';
import LoadingDots from 'components/atoms/LoadingDots';
import Section from 'components/atoms/Section';
import { Heading1, Heading3 } from 'components/atoms/Typography';
import PageContainer from 'components/molecules/PageContainer';
import PostItem from 'components/molecules/PostItem';
import SubscribeBlogForm from 'components/organisms/forms/SubscribeBlogForm';
import BREAKPOINTS from 'lib/constants/breakpoints';
import COLORS from 'lib/constants/colors';
import { PrismicClient } from 'lib/services/prismic';
import { GetStaticProps } from 'next';
import {
  BlogJsonLd,
  BreadcrumbJsonLd,
  NextSeo,
  SiteLinksSearchBoxJsonLd,
} from 'next-seo';
import Image from 'next/image';
import { useRouter } from 'next/router';
import Prismic from 'prismic-javascript';
import { RichText } from 'prismic-reactjs';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { BlogHome, Post } from 'types/frontEndTypes';

const getPosts = async (page: number) => {
  const posts = await PrismicClient.query(
    [
      Prismic.Predicates.at('document.type', 'post'),
      Prismic.Predicates.dateAfter(
        'document.first_publication_date',
        '2021-06-29',
      ),
    ],
    {
      orderings: '[document.first_publication_date desc]',
      pageSize: 12,
      page,
    },
  );

  return posts;
};

const Wrapper = styled(Section)`
  padding-top: 64px;
  padding-bottom: 64px;

  @media screen and (max-width: 768px) {
    padding: 32px 8px;
  }

  position: relative;
  display: flex;
  flex-flow: row;
  flex-wrap: wrap;
`;

const Header = styled.header`
  position: relative;

  display: flex;
  background-color: ${COLORS.black};

  .bgWrap {
    position: absolute;
    overflow: hidden;
    width: 100%;
    height: 100%;
    background-color: ${COLORS.black};
  }

  @media screen and (max-width: ${BREAKPOINTS.medium}) {
  }
`;

const SearchWrapper = styled(GridBox)`
  margin: 16px 0 0;
  justify-content: center;

  @media screen and (max-width: ${BREAKPOINTS.medium}) {
  }
`;

const InnerHeader = styled.div`
  position: relative;
  text-align: center;
  max-width: 900px;
  align-self: center;
  margin: 0 auto;
  padding: 120px 32px 64px;
  color: #fff;

  @media screen and (max-width: 768px) {
  }

  h1 {
    margin-bottom: 24px;
    font-size: 56px;
    @media screen and (max-width: ${BREAKPOINTS.medium}) {
      font-size: 30px;
    }
  }

  p {
    font-size: 18px;
    @media screen and (max-width: ${BREAKPOINTS.medium}) {
    }
  }
`;

const NewsLetterWrapper = styled(GridBox)`
  margin-bottom: 76px;
  padding: 48px 20px;
  text-align: center;
  border-radius: 0.6rem;
  background: ${COLORS.pastel};

  @media screen and (max-width: ${BREAKPOINTS.medium}) {
    padding: 32px 20px;
  }

  h3 {
    font-size: 30px;
  }
`;

interface Props {
  posts: Post[];
  totalPages: number;
  blogHome: BlogHome;
  preview: boolean;
}

const Blog = ({ posts, totalPages, blogHome, preview }: Props) => {
  const nodeRef = useRef<HTMLDivElement>(null);
  const router = useRouter();
  const isVisible = useIsVisible(nodeRef);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [actualPosts, setActualPosts] = useState<Post[]>(posts);

  const blogHomeData = blogHome.data;

  useEffect(() => {
    if (page >= totalPages) return;

    if (isVisible) {
      setLoading(true);
      getPosts(page + 1).then((newPosts) => {
        setActualPosts([...actualPosts, ...(newPosts.results as Post[])]);
        setPage(page + 1);
        setLoading(false);
      });
    }
  }, [isVisible, totalPages, page, actualPosts]);

  const url = env.baseUrl + ROUTES.blog;

  return (
    <PageContainer
      showNavBar
      showFooter
      transparentNavbar
      preview={preview}
      title={blogHomeData.meta_title}
      metaDescription={blogHomeData.meta_description}
    >
      <BreadcrumbJsonLd
        itemListElements={[
          {
            position: 1,
            name: 'Luna',
            item: env.baseUrl,
          },
          {
            position: 2,
            name: 'Blog da Luna',
            item: env.baseUrl + ROUTES.blog,
          },
        ]}
      />
      <BlogJsonLd
        url={env.baseUrl + ROUTES.blog}
        title={blogHomeData.meta_title}
        images={[blogHomeData.image.url || '']}
        dateModified={posts[0]?.last_publication_date || ''}
        datePublished={blogHome.first_publication_date || ''}
        authorName="Luna Seguros"
        description={blogHomeData.meta_description}
      />
      <NextSeo
        openGraph={{
          images: blogHomeData.image.url
            ? [
                {
                  url: blogHomeData.image.url,
                  alt: blogHomeData.image.alt,
                  width: 1200,
                  height: 628,
                },
              ]
            : [],
        }}
      />
      <SiteLinksSearchBoxJsonLd
        url={url}
        potentialActions={[
          {
            target: `${url}/busca?q`,
            queryInput: 'search',
          },
        ]}
      />

      <Header>
        <div className="bgWrap">
          {blogHomeData.image.url && (
            <Image
              alt={blogHomeData.image.alt}
              src={blogHomeData.image.url}
              layout="fill"
              objectFit="cover"
            />
          )}
        </div>
        <InnerHeader>
          <Heading1 style={{ color: '#fff' }}>
            {RichText.asText(blogHomeData.headline)}
          </Heading1>
          <SearchWrapper justifyContent="flex-end">
            <SearchForm
              placeholder="Buscar Posts"
              onSearch={(term) => {
                router.push(`${url}/busca?q=${term}`);
              }}
            />
          </SearchWrapper>
        </InnerHeader>
      </Header>
      <Wrapper>
        {!actualPosts.length && (
          <p
            style={{
              display: 'block',
              textAlign: 'center',
              fontSize: 20,
              padding: '64px 0',
              width: '100%',
            }}
          >
            Nenhum post no momento
          </p>
        )}
        {actualPosts.map((post, index) =>
          index === 3 ? (
            <React.Fragment key={post.id}>
              <NewsLetterWrapper flexDirection="column" justifyContent="center">
                <Heading3>{blogHomeData.subscribe_title}</Heading3>
                <SubscribeBlogForm
                  description={blogHomeData.subscribe_description}
                  origin="blog"
                />
              </NewsLetterWrapper>
              <PostItem post={post} key={post.id} />
            </React.Fragment>
          ) : (
            <PostItem post={post} key={post.id} />
          ),
        )}

        <GridBox justifyContent="center">
          <div ref={nodeRef} />
          {loading && <LoadingDots color={COLORS.pink} />}
        </GridBox>
      </Wrapper>
    </PageContainer>
  );
};

export const getStaticProps: GetStaticProps<Props> = async ({
  preview = false,
  previewData,
}) => {
  const blogDocument = (await PrismicClient.getSingle(
    'blog_home',
    previewData as Record<string, any>,
  )) as BlogHome;

  const posts = await getPosts(1);

  return {
    props: {
      posts: posts ? (posts.results as Post[]) : [],
      totalPages: posts.total_pages,
      blogHome: blogDocument,
      preview,
    },
    revalidate: 3600,
  };
};

export default Blog;
